import { Button, Checkbox, FormControlLabel, FormGroup } from '@material-ui/core';
import { EditorRow } from 'components/common/styles';
import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { ModalWrapper } from '../../../components/ModalWrapper';
import { IDialect } from '../../../store/reducers/courseConfig';
import ModuleContext from '../../../CMFW/context/ModuleContext';
import TranslationDashboardModel from '../../../models/TranslationDashboardModel';

type IProps = {
  onClose: () => void;
  ref_: number;
  startLesson: number;
  endLesson: number;
  sourceDialects: IDialect[];
};

const EXPORT_FIELDS = [
  { key: 'text', label: 'Text' },
  { key: 'pattern', label: 'Pattern' },
  { key: 'status', label: 'Status' },
];

const TranslationDashboardExportModal: React.FC<IProps> = (props) => {
  const [selectedFields, setSelectedFields] = useState<string[]>(['text', 'pattern', 'status']);
  const [selectedDialects, setSelectedDialects] = useState<number[]>(props.sourceDialects.map((d) => d.dialectId));
  const [loading, setLoading] = useState(false);

  const moduleModel = useContext(ModuleContext) as TranslationDashboardModel;

  const handleFieldToggle = (field: string) => {
    setSelectedFields((prev) => (prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]));
  };

  const handleDialectToggle = (dialectId: number) => {
    setSelectedDialects((prev) =>
      prev.includes(dialectId) ? prev.filter((d) => d !== dialectId) : [...prev, dialectId],
    );
  };

  const handleExport = async () => {
    setLoading(true);
    await moduleModel.exportCSV({
      ref: props.ref_,
      startLesson: props.startLesson,
      endLesson: props.endLesson,
      fields: selectedFields,
      dialects: selectedDialects,
    });
    setLoading(false);
    props.onClose();
  };

  const allFieldsSelected = selectedFields.length === EXPORT_FIELDS.length;
  const allDialectsSelected = selectedDialects.length === props.sourceDialects.length;

  const toggleAllFields = () => {
    setSelectedFields(allFieldsSelected ? [] : EXPORT_FIELDS.map((f) => f.key));
  };

  const toggleAllDialects = () => {
    setSelectedDialects(allDialectsSelected ? [] : props.sourceDialects.map((d) => d.dialectId));
  };

  const isDisabled = selectedFields.length === 0 || selectedDialects.length === 0 || loading;

  return (
    <ModalWrapper onClose={props.onClose} title={'Export Translations'} width={400} height={350}>
      <Section>
        <SectionHeader>
          <SectionLabel>Fields</SectionLabel>
          <ToggleLink onClick={toggleAllFields}>{allFieldsSelected ? 'Deselect All' : 'Select All'}</ToggleLink>
        </SectionHeader>
        <FormGroup row>
          {EXPORT_FIELDS.map((field) => (
            <FormControlLabel
              key={field.key}
              control={
                <BlueCheckbox
                  checked={selectedFields.includes(field.key)}
                  onChange={() => handleFieldToggle(field.key)}
                  size="small"
                />
              }
              label={field.label}
            />
          ))}
        </FormGroup>
      </Section>
      <Section>
        <SectionHeader>
          <SectionLabel>Dialects</SectionLabel>
          <ToggleLink onClick={toggleAllDialects}>
            {allDialectsSelected ? 'Deselect All' : 'Select All'}
          </ToggleLink>
        </SectionHeader>
        <FormGroup row>
          {props.sourceDialects.map((dialect) => (
            <FormControlLabel
              key={dialect.dialectId}
              control={
                <BlueCheckbox
                  checked={selectedDialects.includes(dialect.dialectId)}
                  onChange={() => handleDialectToggle(dialect.dialectId)}
                  size="small"
                />
              }
              label={dialect.dialectCode}
            />
          ))}
        </FormGroup>
      </Section>
      <EditorRowStyled>
        <DownloadButton variant="contained" size="small" disabled={isDisabled} onClick={handleExport}>
          <FontAwesomeIcon icon={faDownload} style={{ marginRight: 6 }} />
          {loading ? 'Exporting...' : 'Download CSV'}
        </DownloadButton>
      </EditorRowStyled>
    </ModalWrapper>
  );
};

const Section = styled.div`
  margin-bottom: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const SectionLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1565c0;
`;

const ToggleLink = styled.span`
  font-size: 12px;
  color: #1976d2;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const BlueCheckbox = styled(Checkbox)`
  &.MuiCheckbox-colorSecondary.Mui-checked {
    color: #1976d2;
  }
`;

const DownloadButton = styled(Button)`
  && {
    background-color: #1976d2;
    color: white;
    text-transform: none;
    font-weight: 500;
    &:hover {
      background-color: #1565c0;
    }
    &:disabled {
      background-color: #b0bec5;
      color: white;
    }
  }
`;

const EditorRowStyled = styled(EditorRow)`
  justify-content: flex-end;
  margin-top: 10px;
`;

export default TranslationDashboardExportModal;
